import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { adminUsersApi, type AdminUserSearchHit } from '~/lib/api';
import { ErrorBoundary } from '~/components/ErrorBoundary';

const PAGE_SIZE = 50;

/**
 * Admin Users directory. Browse/search login users and drill into a user's
 * page to manage their manual guild grants. Admin-gated at the route level.
 */
function UsersListContent() {
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [page, setPage] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => {
      setDebounced(search.trim());
      setPage(0);
    }, 250);
    return () => clearTimeout(t);
  }, [search]);

  const isSearching = debounced.length >= 2;

  const listQuery = useQuery({
    queryKey: ['adminUsers', 'list', page],
    queryFn: () => adminUsersApi.list(PAGE_SIZE, page * PAGE_SIZE),
    enabled: !isSearching,
    placeholderData: keepPreviousData,
  });

  const searchQuery = useQuery({
    queryKey: ['adminUsers', 'search', debounced],
    queryFn: () => adminUsersApi.search(debounced),
    enabled: isSearching,
  });

  const users: AdminUserSearchHit[] = isSearching
    ? searchQuery.data?.users ?? []
    : listQuery.data?.users ?? [];
  const total = listQuery.data?.total ?? 0;
  const isLoading = isSearching ? searchQuery.isLoading : listQuery.isLoading;
  const maxPage = Math.max(0, Math.ceil(total / PAGE_SIZE) - 1);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-text-primary">Users</h1>
        <p className="text-sm text-text-secondary mt-1">
          Login users. Open a user to manage their manual guild grants.
        </p>
      </div>

      <div className="card p-6">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search username (min 2 chars)…"
          className="input w-full mb-4"
        />

        {isLoading ? (
          <p className="text-text-muted text-sm">Loading…</p>
        ) : users.length === 0 ? (
          <p className="text-text-muted text-sm">No users found.</p>
        ) : (
          <ul className="divide-y divide-border rounded-lg border border-border overflow-hidden">
            {users.map((u) => (
              <li key={u.id}>
                <Link
                  to={`/settings/users/${u.id}`}
                  className="flex items-center justify-between px-4 py-3 hover:bg-surface-2 transition-colors"
                >
                  <div className="min-w-0">
                    <span className="text-sm text-text-primary">{u.username}</span>
                    {u.role === 'admin' && (
                      <span className="ml-2 text-[10px] font-mono uppercase tracking-wider text-accent">
                        admin
                      </span>
                    )}
                    <div className="text-xs font-mono text-text-muted truncate">{u.id}</div>
                  </div>
                  {(u.manual_grants ?? 0) > 0 && (
                    <span className="shrink-0 text-xs font-mono text-text-secondary bg-surface-3 rounded px-2 py-1">
                      {u.manual_grants} grant{u.manual_grants === 1 ? '' : 's'}
                    </span>
                  )}
                </Link>
              </li>
            ))}
          </ul>
        )}

        {!isSearching && total > PAGE_SIZE && (
          <div className="flex items-center justify-between mt-4">
            <span className="text-xs text-text-muted">
              {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} of {total}
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
                className="btn btn-secondary text-sm"
              >
                Prev
              </button>
              <button
                onClick={() => setPage((p) => Math.min(maxPage, p + 1))}
                disabled={page >= maxPage}
                className="btn btn-secondary text-sm"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function UsersListPage() {
  return (
    <ErrorBoundary>
      <UsersListContent />
    </ErrorBoundary>
  );
}
